// Phase 2 P2-7 + SA-1 — useSlipAutopsy hook with queue-first failure
// handling.
//
// Per SlipAutopsy_Redesign_Brief_v1.0 §01 (Critical Bug) + N01:
//   The hook NEVER surfaces a raw Edge fn error to the UI. Every
//   submission writes to slip_autopsy_queue FIRST, then calls the
//   Edge fn. On any failure (timeout, 4xx, 5xx, network) we mark
//   the queue row as `queued` and the page renders a graceful
//   "Analysis queued — we'll notify you when complete." state.
//
// State machine (visible via `state` field):
//   idle          — fresh mount, nothing submitted yet
//   processing    — queue row written, Edge fn in-flight
//   complete      — Edge fn returned an autopsy result
//   queued        — Edge fn failed; queue row in 'queued' status,
//                   awaiting server-side retry. NEVER an error.
//   error_input   — Local validation failed (empty / malformed
//                   text). Inline, recoverable, NOT a network error.
//
// SA-1 ships with synchronous Edge invocation. SA-2 may add a
// proper background-retry RPC + Realtime subscription so the
// 'queued' state auto-resolves to 'complete' without a page reload.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export type DeclaredOutcome = "won" | "lost" | "partial" | "void" | "unknown";

export type AutopsyState =
  | "idle"
  | "processing"
  | "complete"
  | "queued"
  | "error_input";

export interface ExtractedLeg {
  home_team?: string;
  away_team?: string;
  league?: string;
  market?: string;
  selection?: string;
  odds?: number;
  outcome?: "won" | "lost" | "void" | "pending" | "unknown";
}

export interface FailureReason {
  leg_index: number;
  leg: ExtractedLeg;
  reasons: string[];
}

export interface AutopsyResult {
  upload_id: string;
  extracted_legs: ExtractedLeg[];
  combined_odds: number | null;
  failed_leg_indices: number[];
  failure_reasons: FailureReason[];
  patterns_found: string[];
  recommendations: { kind: string; message: string }[];
}

export interface BettingProfile {
  user_id: string;
  slips_analyzed: number;
  win_rate: number | null;
  most_used_markets: Record<string, number>;
  most_used_leagues: Record<string, number>;
  recommended_avoid: { pattern: string; count: number }[];
  preferred_odds_range: number[] | null;
  avg_legs_per_slip: number | null;
  bookmaker_distribution: Record<string, number>;
  last_updated: string;
}

/** Input shape accepted by upload(). SA-5 added the optional
 * `input_mode` + `slip_legs` fields for the structured leg builder.
 *
 * For paste mode (legacy + SA-5 paste toggle):
 *   { input_mode: 'paste' (default), slip_text, … }
 *
 * For builder mode (SA-5 default):
 *   { input_mode: 'builder', slip_legs: [...], slip_text: '' (or a
 *     fallback serialization), … }
 *
 * The hook writes both `slip_text` and `slip_legs` to the queue row
 * regardless of mode — `slip_legs` is empty for paste, `slip_text`
 * is a derived serialization for builder. SA-6 reads `slip_legs`
 * when present to skip the LLM parser. */
export interface UploadInput {
  slip_text: string;
  /** Structured legs from the SA-5 builder. Mutually informative
   * with `slip_text` — if both are set, builder mode wins. */
  slip_legs?: ReadonlyArray<{
    fixture: string;
    market: string;
    selection: string;
    odds: number | null;
    outcome: "won" | "lost" | "void" | "unknown";
  }>;
  input_mode?: "paste" | "builder" | "upload";
  declared_outcome: DeclaredOutcome;
  declared_stake?: number;
  declared_returns?: number;
  bookmaker_slug?: string;
  /** SA-5b — Upload mode: storage path inside the `slip-images`
   * bucket. Set only when input_mode === 'upload'. The Edge fn
   * (SA-6) will fetch the file via the service role + run vision
   * extraction; until SA-6 ships the queue row stays in 'queued'
   * state with a `vision_pending` note. */
  slip_image_path?: string;
  slip_image_mime?: string;
  slip_image_size_bytes?: number;
}

export interface SlipAutopsyState {
  /** Discriminated state machine — UI branches on this. */
  state: AutopsyState;
  result: AutopsyResult | null;
  profile: BettingProfile | null;
  loadingProfile: boolean;
  /** Only set for state === 'error_input' (local validation). Never
   * holds a technical / network error string. Brief N01. */
  inputError: string | null;
  /** Queue row id of the in-flight or last submission (for retries
   * + Realtime). Null until first submission. */
  queueId: string | null;
  upload: (input: UploadInput) => Promise<void>;
  refreshProfile: () => Promise<void>;
  reset: () => void;
}

/** Local input validation. Returns null if valid, or a short
 * actionable hint string ('We couldn't parse this slip…').
 *
 * SA-5: builder-mode submissions are valid when slip_legs has at
 * least one complete-looking entry (fixture + market + odds), even
 * if slip_text is empty. Paste-mode keeps the SA-1 text-length
 * check. */
function validateInput(input: UploadInput): string | null {
  const text = (input.slip_text ?? "").trim();
  const isBuilder = input.input_mode === "builder";
  const isUpload = input.input_mode === "upload";
  const completeLegs = (input.slip_legs ?? []).filter(
    (l) =>
      l.fixture.trim().length > 0 &&
      l.market.trim().length > 0 &&
      l.selection.trim().length > 0 &&
      l.odds != null &&
      Number.isFinite(l.odds) &&
      l.odds > 0,
  );

  if (isUpload) {
    // SA-5b: upload mode is gated by the dropzone delivering a
    // storage path. The dropzone itself enforces mime + size; this
    // is the last-line defence against a missing path.
    if (!input.slip_image_path || input.slip_image_path.length === 0) {
      return "Add a slip image before running the autopsy.";
    }
    return null;
  }

  if (isBuilder) {
    if (completeLegs.length === 0) {
      return "Add at least one complete leg (fixture, market, selection, odds) before running the autopsy.";
    }
    return null;
  }

  // Paste mode (default).
  if (text.length === 0) {
    return "Add at least one leg before running the autopsy.";
  }
  if (text.length > 12_000) {
    return "Slip text is unusually long. Trim to the actual legs and retry.";
  }
  return null;
}

const EDGE_TIMEOUT_MS = 30_000;

async function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return await new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("timeout")), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });
}

export function useSlipAutopsy(): SlipAutopsyState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [state, setState] = useState<AutopsyState>("idle");
  const [result, setResult] = useState<AutopsyResult | null>(null);
  const [profile, setProfile] = useState<BettingProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [inputError, setInputError] = useState<string | null>(null);
  const [queueId, setQueueId] = useState<string | null>(null);

  const refreshProfile = useCallback(async (): Promise<void> => {
    if (!user) {
      setProfile(null);
      setLoadingProfile(false);
      return;
    }
    setLoadingProfile(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke<{
        ok: boolean;
        profile?: BettingProfile;
      }>("slip-autopsy", { body: { action: "profile", user_id: user.id } });
      // SA-1: even a profile-fetch failure must not surface as a
      // technical error. The profile section degrades to its
      // pre-populated empty state per N04.
      if (!invokeErr && data?.profile) {
        setProfile(data.profile);
      }
    } catch {
      // Same: silent degrade.
    } finally {
      setLoadingProfile(false);
    }
  }, [supabase, user]);

  const upload = useCallback(
    async (input: UploadInput): Promise<void> => {
      if (!user) {
        setState("error_input");
        setInputError("Sign in to analyze a slip.");
        return;
      }

      // ── Local input validation ──
      const validationErr = validateInput(input);
      if (validationErr) {
        setState("error_input");
        setInputError(validationErr);
        return;
      }

      // ── 1. Write to queue BEFORE any Edge call ──
      // If this insert itself fails (e.g. offline), we still don't
      // show a technical error — just keep the user in the
      // processing state. They can retry the submission and the
      // queue insert will succeed.
      setState("processing");
      setInputError(null);
      setResult(null);

      let queue_id: string | null = null;
      // SA-5 — propagate input_mode + slip_legs into the queue row.
      // Defaults preserve the SA-1 behaviour: paste mode with empty
      // legs[] when callers don't pass the new fields.
      const inputMode = input.input_mode ?? "paste";
      const slipLegs = input.slip_legs ?? [];
      try {
        // SA-1: untyped table reference because slip_autopsy_queue
        // is brand-new (migration 20260512100000) and the generated
        // db/types.ts hasn't been refreshed yet. The `as never` cast
        // matches the established pattern from useSikaGoal.ts for
        // tables introduced before a types regen.
        const { data: inserted, error: insertErr } = await supabase
          .from("slip_autopsy_queue" as never)
          .insert({
            user_id: user.id,
            slip_text: input.slip_text.trim(),
            slip_legs: slipLegs,
            input_mode: inputMode,
            outcome: input.declared_outcome,
            stake: input.declared_stake ?? null,
            returns: input.declared_returns ?? null,
            bookmaker: input.bookmaker_slug ?? null,
            // SA-5b — upload mode carries the storage reference so
            // SA-6's vision Edge fn can pull the image. Null for
            // builder + paste.
            slip_image_url: input.slip_image_path ?? null,
            slip_image_mime: input.slip_image_mime ?? null,
            slip_image_size_bytes: input.slip_image_size_bytes ?? null,
            status: "processing",
          } as never)
          .select("queue_id")
          .single();
        if (!insertErr && inserted) {
          queue_id = (inserted as { queue_id?: string }).queue_id ?? null;
          if (queue_id) setQueueId(queue_id);
        }
      } catch {
        // Queue insert failed — proceed to Edge call anyway. The
        // server-side log will note the missing queue row, but the
        // user experience is unchanged.
      }

      // ── 2. Call the Edge fn with timeout ──
      // SA-6: upload mode flows through the same await path as
      // builder/paste. The Edge fn handles vision extraction
      // server-side via gpt-4o; the hook just waits for the result
      // (or falls into the queued branch on timeout / failure).
      try {
        const { data, error: invokeErr } = await withTimeout(
          supabase.functions.invoke<{
            ok: boolean;
            upload_id?: string;
            extracted_legs?: ExtractedLeg[];
            combined_odds?: number | null;
            failed_leg_indices?: number[];
            failure_reasons?: FailureReason[];
            patterns_found?: string[];
            recommendations?: { kind: string; message: string }[];
            error?: string;
          }>("slip-autopsy", {
            body: {
              action: "upload",
              user_id: user.id,
              slip_text: input.slip_text.trim(),
              // SA-5: forward the structured payload too. The Edge
              // fn ignores unknown fields today; SA-6 reads slip_legs
              // when present to skip the LLM parser.
              slip_legs: slipLegs,
              input_mode: inputMode,
              declared_outcome: input.declared_outcome,
              declared_stake: input.declared_stake ?? null,
              declared_returns: input.declared_returns ?? null,
              bookmaker_slug: input.bookmaker_slug ?? null,
              // SA-6 — vision extraction fields. Null for non-upload.
              slip_image_path: input.slip_image_path ?? null,
              slip_image_mime: input.slip_image_mime ?? null,
              slip_image_size_bytes: input.slip_image_size_bytes ?? null,
              queue_id, // pass through so Edge fn can update the row
            },
          }),
          EDGE_TIMEOUT_MS,
        );

        if (invokeErr || !data?.ok) {
          // Edge fn errored — leave queue row in 'queued' state and
          // show the queued UI. Brief N01 — never show the technical
          // error string. Log internally for ops.
          // eslint-disable-next-line no-console
          console.warn(
            "[slip-autopsy] Edge fn returned error; user shown queued state.",
            invokeErr?.message ?? data?.error ?? "unknown",
          );
          if (queue_id) {
            // Best-effort queue-row update (RLS prevents update from
            // authenticated; Edge fn re-tries this server-side. The
            // attempt is harmless if it fails.)
            void supabase
              .from("slip_autopsy_queue" as never)
              .update({ status: "queued" } as never)
              .eq("queue_id", queue_id);
          }
          setState("queued");
          return;
        }

        setResult({
          upload_id: data.upload_id ?? "",
          extracted_legs: data.extracted_legs ?? [],
          combined_odds: data.combined_odds ?? null,
          failed_leg_indices: data.failed_leg_indices ?? [],
          failure_reasons: data.failure_reasons ?? [],
          patterns_found: data.patterns_found ?? [],
          recommendations: data.recommendations ?? [],
        });
        setState("complete");
        await refreshProfile();
      } catch (err) {
        // Timeout, network, exception — all the same: queued state,
        // never a raw error message.
        // eslint-disable-next-line no-console
        console.warn(
          "[slip-autopsy] Edge fn threw; user shown queued state.",
          err instanceof Error ? err.message : String(err),
        );
        if (queue_id) {
          void supabase
            .from("slip_autopsy_queue" as never)
            .update({ status: "queued" } as never)
            .eq("queue_id", queue_id);
        }
        setState("queued");
      }
    },
    [supabase, user, refreshProfile],
  );

  const reset = useCallback((): void => {
    setState("idle");
    setResult(null);
    setInputError(null);
    setQueueId(null);
  }, []);

  useEffect(() => {
    void refreshProfile();
  }, [refreshProfile]);

  return useMemo<SlipAutopsyState>(
    () => ({
      state,
      result,
      profile,
      loadingProfile,
      inputError,
      queueId,
      upload,
      refreshProfile,
      reset,
    }),
    [
      state,
      result,
      profile,
      loadingProfile,
      inputError,
      queueId,
      upload,
      refreshProfile,
      reset,
    ],
  );
}
